/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocationOn, Store } from "@mui/icons-material";
import { formatCEP } from "../../../../services/UtilsService";
import { useDispatch } from "react-redux";
import { RootState } from "../../../../store/store";
import { useAppSelector } from "../../../../hooks/hooks";
import { IAddressState } from "../../../../store/addressSlice";
import { IAssociateState, addAddress } from "../../../../store/associateSlice";
import { RegisterInput, registerSchema } from "./validations/registerSchema";

interface BusinessAddressFormProps {
  handleNextStep(step: string): void;
  handleBackStep(step: string): void;
  close(): void;
}

const BusinessAddressForm: React.FC<BusinessAddressFormProps> = ({
  handleNextStep,
  handleBackStep,
  close,
}) => {
  const {
    register,
    formState: { errors, isSubmitSuccessful },
    reset,
    handleSubmit,
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  });

  const dispatch = useDispatch();

  useEffect(() => {}, [isSubmitSuccessful, reset, handleNextStep, dispatch]);

  const subscriptionInfo = useAppSelector<IAssociateState>(
    (selector) => selector.associateSlice
  );

  const addressState = useAppSelector<IAddressState>(
    (state: RootState) => state.address
  );

  const onSubmitHandler: SubmitHandler<RegisterInput> = (address) => {
    const submitableAddress = {
      ...address,
      IBGECode: addressState?.address?.ibgeCode,
      zipCode: addressState?.address?.zipCode,
    };
    dispatch(addAddress(submitableAddress));
    if (
      subscriptionInfo.subscriptionInfo &&
      subscriptionInfo.subscriptionInfo.ownerPerson.relationships.length !== 0
    ) {
      handleNextStep("associatesList");
      return;
    }

    handleNextStep("associateForm");
    reset();
  };

  return (
    <>
      <Box
        gap={2}
        display="flex"
        sx={{
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          padding: 2,
          background:
            "linear-gradient(229deg, #41B0F8 0%, #626DFD 50.52%, #7232FB 100%)",
        }}
      >
        <div>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              bgcolor: "#9880FD",
              padding: 0.5,
              borderRadius: "20px",
            }}
          >
            <Store
              sx={{
                color: "white",
              }}
            />
          </Box>
        </div>

        <Box>
          <Box display="flex" gap={0.5}>
            <Typography fontWeight="600" fontSize="14px" color="white">
              02
            </Typography>
            <Typography fontWeight="400" fontSize="14px" color="white">
              de 04
            </Typography>
          </Box>
          <Typography fontWeight="600" fontSize="18px" color="white">
            Dados da empresa
          </Typography>
        </Box>
        <Box display="flex" style={{ flex: 1, justifyContent: "flex-end" }}>
          <IconButton>
            <CloseIcon sx={{ color: "#fff" }} onClick={close} />
          </IconButton>
        </Box>
      </Box>
      <Box padding={3} display="flex" flexDirection="column" gap={2}>
        <Typography fontWeight="400" fontSize="16px" color="black">
          Agora nos forneça o endereço completo.
        </Typography>

        <Box display="flex">
          <Box
            border={1}
            display="flex"
            paddingX={2}
            paddingY={0.5}
            borderRadius={6}
            borderColor="#E9EBF0"
            gap={1}
          >
            <LocationOn color="primary" />

            <Typography fontWeight="400" fontSize="16px" color="black">
              CEP {formatCEP(addressState?.address?.zipCode)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        paddingX={2}
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              {...register("address")}
              label="Logradouro"
              required
              defaultValue={addressState?.address?.address}
              error={!!errors["address"]}
              helperText={errors["address"] ? errors["address"].message : ""}
              name="address"
              variant="standard"
              inputProps={{
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              fullWidth
              {...register("neighborhood")}
              defaultValue={addressState?.address?.neighborhood}
              label="Bairro"
              required
              name="neighborhood"
              error={!!errors["neighborhood"]}
              helperText={
                errors["neighborhood"] ? errors["neighborhood"].message : ""
              }
              variant="standard"
              inputProps={{
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              {...register("state")}
              defaultValue={addressState?.address?.state}
              label="Estado"
              required
              name="state"
              error={!!errors["state"]}
              helperText={errors["state"] ? errors["state"].message : ""}
              variant="standard"
              inputProps={{
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              {...register("city")}
              defaultValue={addressState?.address?.city}
              label="Cidade"
              required
              name="city"
              error={!!errors["city"]}
              helperText={errors["city"] ? errors["city"].message : ""}
              variant="standard"
              inputProps={{
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              {...register("number")}
              defaultValue={addressState?.address?.number}
              label="Número"
              required
              name="number"
              error={!!errors["number"]}
              helperText={errors["number"] ? errors["number"].message : ""}
              variant="standard"
              inputProps={{
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              fullWidth
              {...register("additionalInfo")}
              defaultValue={addressState?.address?.additionalInfo}
              label="Complemento"
              name="additionalInfo"
              error={!!errors["additionalInfo"]}
              helperText={
                errors["additionalInfo"] ? errors["additionalInfo"].message : ""
              }
              variant="standard"
              inputProps={{
                style: {
                  fontSize: 16,
                  height: 30,
                },
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            margin: 2,
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              handleBackStep("zipCode");
            }}
          >
            Voltar
          </Button>

          <Button type="submit" variant="contained" sx={{ marginLeft: 1 }}>
            Próximo
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default BusinessAddressForm;
